/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 * keep in sync with the code):
 *   role:      lib util — dev-mode tenant/org selection (localStorage-backed);
 *              computes the API host from an org slug or an explicit host:port
 *   extends:   -
 *   template:  none
 *   args:      -
 *   uses:      window.localStorage
 *   siblings:  -
 *   tests:     dev-org-test.js (unit)
 *
 * @module beebee-base/lib/dev-org
 * @description Helper per la selezione del tenant/organizzazione in sviluppo.
 *
 * In sviluppo l'host API è derivato dall'organizzazione salvata in localStorage
 * (`beebee_dev_org`) invece che essere hardcoded in `config/environment.js`.
 * Usato dall'instance-initializer `dev-host` (al boot) e dal componente
 * `dev-toolbar` (cambio org a runtime). NON ha effetto in produzione.
 */

/** Chiave localStorage che contiene lo slug dell'organizzazione di sviluppo */
const DEV_ORG_KEY = 'beebee_dev_org';

/**
 * Legge lo slug dell'organizzazione di sviluppo da localStorage.
 * @returns {string|null} slug org (es. "fisiotest") o null se non impostato
 */
function getDevOrg() {
  try {
    return window.localStorage.getItem(DEV_ORG_KEY) || null;
  } catch (e) {
    return null;
  }
}

/**
 * Salva lo slug dell'organizzazione di sviluppo in localStorage.
 * Il nuovo host viene applicato al successivo reload (vedi instance-initializer).
 * @param {string} org - slug org (es. "fisiotest")
 */
function setDevOrg(org) {
  try {
    if (org) {
      window.localStorage.setItem(DEV_ORG_KEY, org.trim());
    } else {
      window.localStorage.removeItem(DEV_ORG_KEY);
    }
  } catch (e) {
    // localStorage non disponibile: ignora
  }
}

/**
 * Calcola l'host API a partire dal valore inserito. Supporta:
 *  - slug organizzazione       → "demo"            → https://demo.beebeeboard.com/api
 *  - host locale con porta      → "localhost:3001"  → https://localhost:3001/api
 *  - IP con porta               → "192.168.1.2:3001"→ https://192.168.1.2:3001/api
 *
 * Accetta anche valori già completi (con protocollo e/o "/api" finale).
 *
 * @param {string} value - slug org oppure host[:porta]
 * @returns {string|null} base host API (es. "https://demo.beebeeboard.com/api") o null
 */
function computeHost(value) {
  if (!value) {
    return null;
  }

  // Normalizza: togli protocollo, slash e "/api" finale.
  let v = value.trim();
  v = v.replace(/^https?:\/\//i, '');
  v = v.replace(/\/+$/, '');
  v = v.replace(/\/api$/i, '');
  v = v.replace(/\/+$/, '');
  if (!v) {
    return null;
  }

  // host locale / IP: contiene porta (":"), un punto, oppure è "localhost".
  const isHost = v.includes(':') || v.includes('.') || v.toLowerCase() === 'localhost';
  return isHost ? `https://${v}/api` : `https://${v}.beebeeboard.com/api`;
}

export { DEV_ORG_KEY, computeHost, getDevOrg, setDevOrg };
//# sourceMappingURL=dev-org.js.map
